/**
 * Aircraft & Marine layer — marine half (L.2.2, R.1d render). The marine sibling
 * of the inline aircraft render, mounted alongside it under the same "Aircraft &
 * Marine" toggle. Per §3B.8 the marine family is the AMBIENT register: a
 * heading-aware, per-class bow-up silhouette (vesselSilhouettes), a single
 * desaturated marine hue, freshness conveyed by OPACITY (ais_visibility), and it
 * NEVER pulses.
 *
 * Imperative installer (MapView owns the viewer): requestRefresh on camera moves,
 * setVisible on the toggle (shares showLiveAircraft), pick from the central click
 * handler → VesselFeature → the marine detail panel.
 */
import * as Cesium from 'cesium';
import { fetchVessels, type BboxQuery, type VesselFeature, type VesselFeatureCollection } from '../api';
import { headingToBillboardRotation } from '../aircraftStyles';
import { classifyVessel } from '../vesselStyles';
import { vesselSilhouetteUrl } from '../vesselSilhouettes';

const REFRESH_DEBOUNCE_MS = 300;
const VESSEL_PREFIX = 'vessel:';

export interface MarineVesselsLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<VesselFeatureCollection>;
}

export interface MarineVesselsLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  pick(entity: unknown): VesselFeature | null;
  destroy(): void;
}

export function installMarineVesselsLayer(
  viewer: Cesium.Viewer,
  opts: MarineVesselsLayerOptions = {},
): MarineVesselsLayerHandle {
  const fetcher = opts.fetcher ?? fetchVessels;
  const ds = new Cesium.CustomDataSource('marine-vessels');
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, VesselFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (fc: VesselFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    for (const feature of fc.features) {
      const p = feature.properties;
      const style = classifyVessel(p);
      const [lon, lat] = feature.geometry.coordinates;
      const icon = vesselSilhouetteUrl(p.vessel_type, style.color);
      const heading = p.heading_deg;
      const entity = ds.entities.add({
        id: `${VESSEL_PREFIX}${feature.id}`,
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        billboard: {
          image: icon,
          width: style.size,
          height: style.size,
          // Bow-up silhouette → negate compass heading (Cesium CCW-positive). No
          // heading → bow north (rotation 0). Static: vessels never pulse.
          rotation: headingToBillboardRotation(heading),
          alignedAxis: Cesium.Cartesian3.ZERO,
          color: Cesium.Color.WHITE.withAlpha(style.opacity),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
        },
      });
      entityToFeature.set(entity, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchVessels failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): VesselFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
